import { FontDownload, FontDownloadOutlined } from '@mui/icons-material';
import { Box, useMediaQuery, Theme, CircularProgress } from '@mui/material';
import AutoLinksModule from 'quill-auto-links';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { StyledButtonGroup, StyledReactQuill } from './RichTextEditor.style';
import { DropzoneState } from 'react-dropzone';
import AttachmentIconButton from '../commentInput/attachmentIconButton';
import SubmitIconButton from '../commentInput/submitIconButton';
import { StyledIconButton } from '../commentInput/commentInput.style';
import delta from 'quill-delta';

const AutoLinks = (AutoLinksModule as any).default || AutoLinksModule;
Quill.register('modules/autoLinks', AutoLinks);

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
];

interface RichTextEditorProps {
  value: string;
  placeholder?: string;
  onChange?: (html: string) => void;
  onKeyPress?: (e: any) => void;
  onSubmit?: (value: string) => void;
  dropzoneState: DropzoneState;
  disabledAttachment?: boolean;
  setIsOpenAttachment?: React.Dispatch<React.SetStateAction<boolean>>;
  isOpenAttachment?: boolean;
  loading?: boolean;
}

export default function RichTextEditor(props: RichTextEditorProps) {
  const { value, placeholder, onChange, onKeyPress, onSubmit, dropzoneState, disabledAttachment, setIsOpenAttachment, isOpenAttachment, loading } = props;

  const initValue = value.replace(/<p><br\s*\/><\/p>/g, '')
                        .replace(/<p>/g, '')
                        .replace(/<\/p>/g, "\n")
                        .replace(/[^\S\r\n]{2,}/g, ' ');

  const isSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [showToolbar, setShowToolbar] = useState(false);

  const quillRef = useRef<ReactQuill>(null);
  const valueRef = useRef<string>(value);

  const checkValueValid = () => !!quillRef.current?.editor?.root.textContent?.trim();
  const isValidValue = checkValueValid();

  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      editor.root.dataset.placeholder = !!placeholder ? placeholder : "Add a comment";
    }
  }, [placeholder]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const handleSubmit = () => {
    const isValidValue = checkValueValid();
    if (!isValidValue) return;
    const refValue = valueRef.current || '';
    const submitValue = refValue.replace(/<p><br><\/p>/g, '');
    onSubmit?.(submitValue);
    quillRef.current?.editor?.setContents(new delta().insert(''));
  };

  const modules = useMemo(() => {
    return {
      toolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        ['blockquote', 'link'],
        ['clean'],
      ],
      keyboard: {
        bindings: {
          enter: {
            key: 'Enter',
            handler: handleSubmit,
          },
        },
      },
      autoLinks: true,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box position="relative">
      <StyledReactQuill showToolbar={showToolbar} isMobile={isSmDown}>
        <ReactQuill
          tabIndex={0}
          ref={quillRef}
          theme="snow"
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          onChange={onChange}
          onKeyPress={onKeyPress}
          defaultValue={new delta().insert(initValue)}
          id="txt-comment-input"
        />
      </StyledReactQuill>

      <StyledButtonGroup isMobile={isSmDown}>
          <StyledIconButton tabIndex={0} onClick={() => setShowToolbar((x) => !x)} aria-label='Rich text editor icon button' id="btn-comment-editor">
            {showToolbar ? <FontDownload color="primary" /> : <FontDownloadOutlined />}
          </StyledIconButton>

        <AttachmentIconButton tabIndex={0} dropzoneState={dropzoneState} disabled={disabledAttachment} setIsOpenAttachment={setIsOpenAttachment} isOpenAttachment={isOpenAttachment}/>

        {loading ? (
          <CircularProgress size={30} />
        ) : (
          <SubmitIconButton
            tabIndex={0}
            disabled={!isValidValue}
            onClick={handleSubmit}
            id="btn-comment-submit"
          />
        )}
      </StyledButtonGroup>
    </Box>
  );
}
